// @ts-nocheck
/*
  Backform
  http://github.com/amiliaapp/backform

  Component that makes creating form fields like a breeze.

  Copyright (c) 2014 Amilia Inc.
  Written by Martin Drapeau
  Modified for Inbooma to work with browserify & Foundation

  @todo Refactor / Re-develop: 
  - On calling backform.render, return a list of components
  - Better way of styling / modifying components / passing options
  - Better way of dealing with computed fields that are not part of model.attributes
  - Nice to have: Use virtual DOM library to speed up form rendering (e.g. Meteor.js)
  
  Licensed under the MIT @license

 */

import { $, _, Backbone, Radio, Datepicker, moment, select2 } from "../../vendor";
import { AppointmentPicker, OpeningTimes } from "../utils";
import Inboomai18n from "../utils/Inboomai18n";
import InboomaSelectBox from "./InboomaSelectBox";
import * as Sync from "../Sync";

const appChannel = Radio.channel("app");

// Backform namespace and global options
let tempSize: any,
  extraTopClasses: any,
  Backform: any = {
    /* @TODO Append aria-tag
aria-describedby="passwordHelpText">
</label>
<p class="help-text" id="passwordHelpText">Your 
to inputs with help-text */

    // HTML markup global class names. More can be added by individual controls
    // using _.extend. Look at RadioControl as an example.
    formClassName: "inb-form form-horizontal",
    groupClassName: "inb-form-group column", // Sizes can be passed via field.size-attribute
    controlLabelClassName: "inb-form__controlLabel",
    controlsClassName: "inb-form__controllClass",
    controlClassName: "form-control",
    helpClassName: "help-text",
    errorClassName: "form-error",
    errorDetailsClassName: "errors-details",
    helpMessageClassName: "help-text",
    hiddenClassName: "hide",
    requiredInputClassName: undefined,

    // https://github.com/wyuenho/backgrid/blob/master/lib/backgrid.js
    resolveNameToClass: function (name: any, suffix: any): any {
      if (_.isString(name)) {
        let key: any =
          _.map(name.split("-"), function (e) {
            return e.slice(0, 1).toUpperCase() + e.slice(1);
          }).join("") + suffix;
        let klass: any = Backform[key];
        if (_.isUndefined(klass)) {
          throw new ReferenceError('Class "' + key + '" not found');
        }
        return klass;
      }
      return name;
    },
  };

// @see https://stackoverflow.com/questions/47775041/disable-autofill-in-chrome-63

// Backform Form view
// A collection of field models.
let Form: any = (Backform.Form = Backbone.View.extend({
  fields: undefined,
  errorModel: undefined,
  tagName: "form",
  className: function (): any {
    return Backform.formClassName;
  },
  initialize: function (options: any): void {
    if (!(options.fields instanceof Backbone.Collection))
      options.fields = new Fields(options.fields || this.fields);
    this.fields = options.fields;
    this.model.errorModel = options.errorModel || this.model.errorModel || new Backbone.Model();
    this.controls = [];
    this.showRequiredAsAsterisk = options.showRequiredAsAsterisk;
  },
  cleanup: function (): void {
    _.each(this.controls, function (c) {
      c.remove();
    });
    this.controls.length = 0;
  },
  remove: function (): void {
    /* First do the clean up */
    this.cleanup();
    // @ts-ignore
    Backbone.View.prototype.remove.apply(this, arguments);
  },
  render: function (): any {
    this.cleanup();
    this.$el.empty();

    let form: any = this,
      $form: any = this.$el,
      model: any = this.model,
      controls: any = this.controls;

    this.fields.each(function (field: any): void {
      if (typeof field.get("size") !== "undefined") {
        tempSize = field.get("size");
      } else {
        tempSize = [];
      }

      if (typeof field.get("extraTopClasses") !== "undefined") {
        extraTopClasses = field.get("extraTopClasses");
      } else {
        extraTopClasses = [];
      }

      let control: any = new (field.get("control"))({
        field,
        model,
        showAsterisk: form.showRequiredAsAsterisk && field.get("required"),
      });

      if (typeof field.get("rbac") !== "undefined") {
        control.$el.attr("data-inb-action-link", field.get("rbac"));
      }

      $form.append(control.render().$el);
      controls.push(control);
    });

    return this;
  },
}));

// Converting data to/from Model/DOM.
// Stolen directly from Backgrid's CellFormatter.
// Source: http://backgridjs.com/ref/formatter.html
/**
     Just a convenient class for interested parties to subclass.

     The default Cell classes don't require the formatter to be a subclass of
     Formatter as long as the fromRaw(rawData) and toRaw(formattedData) methods
     are defined.

     @abstract
     @class Backform.ControlFormatter
     @constructor
  */
let ControlFormatter = (Backform.ControlFormatter = function (): void {});
_.extend(ControlFormatter.prototype, {
  /**
       Takes a raw value from a model and returns an optionally formatted string
       for display. The default implementation simply returns the supplied value
       as is without any type conversion.

       @member Backform.ControlFormatter
       @param {*} rawData
       @param {Backbone.Model} model Used for more complicated formatting
       @return {*}
    */
  fromRaw: function (rawData: any, model: any): any {
    return rawData;
  },

  /**
       Takes a formatted string, usually from user input, and returns a
       appropriately typed value for persistence in the model.

       If the user input is invalid or unable to be converted to a raw value
       suitable for persistence in the model, toRaw must return `undefined`.

       @member Backform.ControlFormatter
       @param {string} formattedData
       @param {Backbone.Model} model Used for more complicated formatting
       @return {*|undefined}
    */
  toRaw: function (formattedData: any, model: any): any {
    return formattedData;
  },
});

// Store value in DOM as stringified JSON.
let JSONFormatter: () => void = (Backform.JSONFormatter = function () {});
_.extend(JSONFormatter.prototype, {
  fromRaw: function (rawData: any, model: any): string {
    return JSON.stringify(rawData);
  },
  toRaw: function (formattedData: any, model: any): string {
    return JSON.parse(formattedData);
  },
});

// Field model and collection
//
//   A field maps a model attriute to a control for rendering and capturing
//   user input.
let Field: any = (Backform.Field = Backbone.Model.extend({
  defaults: {
    // Name of the model attribute
    // - It accepts "." nested path (e.g. x.y.z)
    name: "",
    // Placeholder for the input
    placeholder: "",
    // Disable the input control
    // (Optional - true/false/function returning boolean)
    // (Default Value: false)
    disabled: false,
    // Visible
    // (Optional - true/false/function returning boolean)
    // (Default Value: true)
    visible: true,
    // Value Required (validation)
    // (Optional - true/false/function returning boolean)
    // (Default Value: true)
    required: false,
    // Default value for the field
    // (Optional)
    value: undefined,
    // Control or class name for the control representing this field
    control: undefined,
    formatter: undefined,
    pattern: undefined, // Foundation Abide pattern -> https://foundation.zurb.com/sites/docs/abide.html
  },
  initialize: function (attributes: any, options: any): void {
    let control: any = Backform.resolveNameToClass(this.get("control"), "Control");
    this.set({ control: control }, { silent: true });
  },
}));

let Fields: any = (Backform.Fields = Backbone.Collection.extend({
  model: Field,
}));

// Base Control class
let Control: any = (Backform.Control = Backbone.View.extend({
  // Additional field defaults
  defaults: {},
  className() {
    // Set Inbooma- and Foundtion-specific attributes

    const large: string =
        "medium-" +
        (typeof tempSize["large"] !== "undefined"
          ? tempSize["large"]
          : typeof tempSize["small"] !== "undefined"
          ? tempSize["small"]
          : "12"),
      small: string =
        "small-" + (typeof tempSize["small"] !== "undefined" ? tempSize["small"] : "12");

    return Backform.groupClassName + " " + extraTopClasses.join(" ") + " " + large + " " + small;
  },
  events: {
    "keydown :input": "processTab",
  },
  template: _.template(
    [
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <span class="<%=Backform.controlClassName%> uneditable-input">',
      "    <%=value%>",
      "  </span>",
      "</div>",
    ].join("\n")
  ),
  initialize: function (options: any): void {
    // Back-reference to the field
    this.field = options.field;
    this.showAsterisk = options.showAsterisk;

    if (typeof this.field.get("showAsterisk") !== "undefined") {
      this.showAsterisk = this.field.get("showAsterisk");
    }

    let formatter: any = Backform.resolveNameToClass(
      this.field.get("formatter") || this.formatter,
      "Formatter"
    );
    if (!_.isFunction(formatter.fromRaw) && !_.isFunction(formatter.toRaw)) {
      formatter = new formatter();
    }
    this.formatter = formatter;

    let attrArr: any = this.field.get("name").split(".");
    let name: any = attrArr.shift();

    // Listen to the field in the model for any change
    this.listenTo(this.model, "change:" + name, this.render);

    // Listen for the field in the error model for any change
    if (this.model.errorModel instanceof Backbone.Model)
      this.listenTo(this.model.errorModel, "change:" + name, this.updateInvalid);
  },
  formatter: ControlFormatter,
  getValueFromDOM: function () {
    return this.formatter.toRaw(this.$el.find(".uneditable-input").text(), this.model);
  },
  onChange: function (e: any): void {
    let model: any = this.model,
      $el: JQuery<HTMLElement> = $(e.target),
      attrArr: any = this.field.get("name").split("."),
      name: any = attrArr.shift(),
      path: any = attrArr.join("."),
      value: any = this.getValueFromDOM(),
      changes: any = {};

    if (this.model.errorModel instanceof Backbone.Model) {
      if (_.isEmpty(path)) {
        this.model.errorModel.unset(name);
      } else {
        let nestedError: any = this.model.errorModel.get(name);
        if (nestedError) {
          this.keyPathSetter(nestedError, path, null);
          this.model.errorModel.set(name, nestedError);
        }
      }
    }

    changes[name] = _.isEmpty(path) ? value : $.extend(true, {}, model.get(name)) || {};

    if (!_.isEmpty(path)) this.keyPathSetter(changes[name], path, value);
    this.stopListening(this.model, "change:" + name, this.render);
    model.set(changes);
    this.listenTo(this.model, "change:" + name, this.render);
  },
  onChangeSelect: function (e: any): void {
    // this allows to abort a change of selectbox
    let msg: any = this.field.get("confirmMessage");
    if (typeof msg !== "undefined" && msg != null && msg != "") {
      if (confirm(msg)) {
        this.onChange(e); // call original event handler if confirmed
      } else {
        let currentModelStatus: string = '"' + this.model.get("status") + '"';
        for (let i: number = 0; i < e.target.options.length; i++) {
          if (e.target.options[i].value == currentModelStatus) {
            e.target.selectedIndex = i; // reset selectbox
            return;
          }
        }
      }
    } else {
      this.onChange(e); // call original event handler
    }
  },
  renderAsteriskIfRequired(): void {
    if (this.showAsterisk) {
      this.$el.find(".inb-form__controlLabel").append(" *");
    }
  },
  render(): any {
    let field: any = _.defaults(this.field.toJSON(), this.defaults),
      attributes: any = this.model.toJSON(),
      attrArr: any = field.name.split("."),
      name: any = attrArr.shift(),
      path: any = attrArr.join("."),
      rawValue: any = this.keyPathAccessor(attributes[name], path),
      data: any = _.extend(field, {
        rawValue: rawValue,
        value: this.formatter.fromRaw(rawValue, this.model),
        attributes: attributes,
        formatter: this.formatter,
      }),
      evalF: (f: any, m: any) => boolean = function (f: any, m: any): boolean {
        return _.isFunction(f) ? !!f(m) : !!f;
      };

    /*Approach towards smart rendering
      if (typeof this._isRendered !== 'undefined') {

        if (rawValue == this.model.get(field.name)) {
          console.log(field, this.model.get('_return_locations'), this.model.changed._return_locations);

          if (field.name == 'begin' || field.name == 'end') {

          }
          return this;
        }

      }*/

    // Evaluate the disabled, visible, and required option
    _.extend(data, {
      disabled: evalF(data.disabled, this.model),
      visible: evalF(data.visible, this.model),
      required: evalF(data.required, this.model),
    });

    if (_.isFunction(data.extraAttributes)) {
      data.extraAttributes = data.extraAttributes(this.model);
    }

    if (_.isUndefined(data.extraAttributes)) {
      data.extraAttributes = [];
    }

    // required to set e.g. time field
    data.moment = moment;

    // Clean up first
    // Arghhh... this hurts...
    // VirtualDOM where is u?
    this.$el.removeClass(Backform.hiddenClassName);

    if (!data.visible) {
      this.$el.addClass(Backform.hiddenClassName);
    }

    if (Backform.requiredInputClassName) {
      this.$el.removeClass(Backform.requiredInputClassName);
    }

    if (data.required) {
      this.$el.addClass(Backform.requiredInputClassName);
    }

    // Added for inbooma
    data.Backform = Backform;

    this.$el.html(this.template(data)).addClass(field.name);
    this.renderAsteriskIfRequired();
    this.updateInvalid();

    if (typeof this.onRender === "function") {
      this.onRender();
    }

    return this;
  },
  clearInvalid: function (): any {
    this.$el
      .removeClass(Backform.errorClassName)
      .find("." + Backform.helpClassName + ".error")
      .remove();
    return this;
  },
  updateInvalid: function (): any {
    let self: any = this,
      errorModel: any = this.model.errorModel,
      handledErrors: any[] = [];
    if (!(errorModel instanceof Backbone.Model)) return this;

    this.clearInvalid();

    this.$el
      .find(":input")
      .not("button")
      .each(
        $.proxy(function (ix: any, el: any): void {
          let error: any = self.keyPathAccessor(errorModel.toJSON(), $(el).attr("name"));

          if (_.isEmpty(error) || _.indexOf(handledErrors, error) >= 0) return;

          self.$el
            .addClass(Backform.errorClassName)
            .find("." + Backform.controlsClassName)
            .append(
              '<span class="' +
                Backform.helpClassName +
                ' error">' +
                this.getErrorAsHtml(error) +
                "</span>"
            )
            .find("." + Backform.errorDetailsClassName + ' [data-toggle="popover"]')
            .popover();

          handledErrors.push(error);
        }, this)
      );

    return this;
  },
  /**
   * Return an error as HTML
   * @param {string|string[]|{Errors: string|string[], Details: string, DetailsLabel: string}} error The error to convert as HTML
   * Expected formats:
   * - String
   * - Array of string
   * - Object with:
   *   - [required] Errors (string or array of string) List of errors
   *   - [optional] Details (HTML string) Details to display in a popover
   *   - [optional] DetailsLabel (string) Text of the link to open the details popover. Default to "?"
   * @returns {string}
   */
  getErrorAsHtml: function (error: any): string {
    let template: _.CompiledTemplate = _.template(
      [
        "<% if (_.isObject(error) && !_.isUndefined(error.Errors)) { %>",
        "  <% if (error.Errors) { %>",
        "    <%= error.Errors.join(', ') %>",
        "  <% } %>",
        "  <% if (!_.isUndefined(error.Details) && $.trim(error.Details)) { %>",
        '    <div class="<%- Backform.errorDetailsClassName %>">',
        '      <button type="button" class="btn btn-link btn-xs" data-html="true" data-toggle="popover" data-placement="bottom" data-content="<%- $.trim(error.Details) %>">',
        "        <%= error.DetailsLabel || '?' %>",
        "      </button>",
        "    </div>",
        "  <% } %>",
        "<% } else if (!_.isObject(error) || _.isArray(error)) { %>",
        "  <% error = _.isArray(error) ? error : [error]; %>",
        "  <%= error.join(', ') %>",
        "<% } %>",
      ].join("\n")
    );

    return template({ error: error });
  },
  keyPathAccessor: function (obj: any, path: any): any {
    let res: any = obj;
    path = path.split(".");
    for (let i: number = 0; i < path.length; i++) {
      if (_.isNull(res)) return null;
      if (_.isEmpty(path[i])) continue;
      if (!_.isUndefined(res[path[i]])) {
        res = res[path[i]];
      } else {
        return null;
      }
    }
    return res;
  },
  keyPathSetter: function (obj: any, path: any, value: any): any {
    path = path.split(".");
    while (path.length > 1) {
      if (!obj[path[0]]) obj[path[0]] = {};
      obj = obj[path.shift()];
    }
    return (obj[path.shift()] = value);
  },
  processTab: function (e: any): void {
    if (e.which == 9) {
      let $target: JQuery<HTMLElement> = $(e.currentTarget);
      setTimeout(function (): void {
        let $nextFocus: JQuery<HTMLElement>;
        if (e.shiftKey) {
          $nextFocus = !!$target.prevAll(":input:visible").length
            ? $target.prevAll(":input:visible").first()
            : $target
                .closest("." + Backform.groupClassName.split(" ")[0] + ":visible")
                .prev("." + Backform.groupClassName.split(" ")[0] + ":visible")
                .find(":input:visible");
        } else {
          $nextFocus = !!$target.nextAll(":input:visible").length
            ? $target.nextAll(":input:visible").first()
            : $target
                .closest("." + Backform.groupClassName.split(" ")[0] + ":visible")
                .next("." + Backform.groupClassName.split(" ")[0] + ":visible")
                .find(":input:visible");
        }

        if ($nextFocus.length) $nextFocus.first().focus();
      }, 0);
    }
  },
}));

// Built-in controls

let UneditableInputControl: any = (Backform.UneditableInputControl = Control);

let HelpControl: any = (Backform.HelpControl = Control.extend({
  template: _.template(
    [
      '<label class="<%=Backform.controlLabelClassName%>">&nbsp;</label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <span class="<%=Backform.helpMessageClassName%> help-block"><%=label%></span>',
      "</div>",
    ].join("\n")
  ),
}));

let SpacerControl: any = (Backform.SpacerControl = Control.extend({
  template: _.template(["<hr />"].join("\n")),
}));

let FillableControl: any = (Backform.FillableControl = Control.extend({
  // DO NOTHING, JUST RENDER DIV WITH NAME AS CLASS-NAME
  template: _.template([""].join("\n")),
}));

let TextareaControl: any = (Backform.TextareaControl = Control.extend({
  defaults: {
    label: "",
    maxlength: 4000,
    extraClasses: [],
    helpMessage: null,
  },
  template: _.template(
    [
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <textarea class="<%=Backform.controlClassName%> <%=extraClasses.join(\' \')%>" name="<%=name%>" maxlength="<%=maxlength%>" placeholder="<%-placeholder%>" <%=disabled ? "disabled" : ""%> <%=required ? "required" : ""%>><%-value%></textarea>',
      "  <% if (helpMessage && helpMessage.length) { %>",
      '    <span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "  <% } %>",
      "</div>",
    ].join("\n")
  ),
  events: _.extend({}, Control.prototype.events, {
    "change textarea": "onChange",
    "focus textarea": "clearInvalid",
  }),
  getValueFromDOM: function (): any {
    return this.formatter.toRaw(this.$el.find("textarea").val(), this.model);
  },
}));

let InboomaselectControl: any = (Backform.InboomaselectControl = Control.extend({
  defaults: {
    collection: null,
    placeholder: "",
    sortBy: "id",
    sortDirection: "asc",
    selectedItem: null,
    listItemContent: _.noop,
    onSelectItem: _.noop,
    advancedUserList: false,
    quickOrder: false,
    inputStyle: "",
  },

  template: _.template(`
      <label class="inb-form__controlLabel"><%= label %></label>
      <div class="select-box-wrapper"></div>
    `),

  render(): any {
    InputControl.prototype.render.apply(this, arguments);

    const that: any = this,
      options: any = this.field.attributes,
      collection: any = Radio.channel(options.collection).request("get:data");

    this.input = new InboomaSelectBox({
      el: this.$el.find(".select-box-wrapper"),
      collection,
      sortBy: options.sortBy,
      sortDirection: options.sortDirection,
      selectedItem: this.model.get(options.name),
      placeholder: options.placeholder,
      advancedUserList: options.advancedUserList,
      quickOrder: options.quickOrder,
      inputStyle: options.inputStyle,
      listItemContent: (model: any): any => options.listItemContent(model),
      onSearch: (query: any): void => {
        collection._filterParams = { q: query };
        collection._skipPagination = true;

        collection.fetchNextPage(_.noop);
      },
      onSelectItem: (model: any): void => {
        if (typeof model === "undefined") return;
        that.model.set(options.name, model.get("id"));

        if (typeof options.onSelectItem !== "undefined") {
          options.onSelectItem(model);
        }
      },
    });

    return this;
  },
}));

let SelectorControl: any = (Backform.SelectorControl = Control.extend({
  defaults: {
    label: "",
    options: [], // List of options as [{label:<label>, value:<value>}, ...]
    extraClasses: [],
    helpMessage: null,
    entityFormAttached: false,
    hasMultiple: () => false,
    forceSelectedOption: () => false,
  },
  template: _.template(
    [
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <select class="<%=Backform.controlClassName%> <%=extraClasses.join(\' \')%>" name="<%=name%>" <%=disabled ? "disabled" : ""%> <%=required ? "required" : ""%> <%= hasMultiple() ? "multiple=\'multiple\'" : "" %> >',
      '   <% if (typeof forceSelectedOption().label !== "undefined") { %>',
      '      <option value="<%- forceSelectedOption().value %>" selected="selected"><%- forceSelectedOption().label %></option>',
      "   <% } %>",
      "    <% for (var i=0; i < options.length; i++) { %>",
      "      <% var option = options[i]; %>",
      '      <option value="<%-formatter.fromRaw(option.value)%>" <%= ((typeof rawValue == "object" ? (_.where(rawValue, { id : Number(option.value) }).length >= 1) : option.value === rawValue) || (forceFirstSelected && i === 0)) ? "selected=\'selected\'" : ""%> <%=option.disabled ? "disabled=\'disabled\'" : ""%>><%-option.label%></option>',
      "    <% } %>",
      "  </select>",
      "</div>",
      "<% if (helpMessage && helpMessage.length) { %>",
      '<span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "<% } %>",
    ].join("\n")
  ),

  events: _.extend({}, Control.prototype.events, {
    "change select": "onChange",
    "focus select": "clearInvalid",
  }),

  _destroySelectField() {
    if (typeof this.selectField !== "undefined") {
      this.selectField.select2("destroy");
    }
  },

  render(): any {
    const that: any = this;

    // We always NEED one selected entity, so let's force it
    // Mostly required for non-saved models
    // Needs tests
    const field: any = this.model.get(this.field.get("name"));

    if (
      (field == "" || field == null || typeof field === "undefined") &&
      this.field.get("options").length >= 1 &&
      typeof this.field.get("forceFirstSelected") !== "undefined"
    ) {
      this.field.set("forceFirstSelected", true);

      if (field !== null && typeof field === "object") {
        this.model
          .get(this.field.get("name"))
          .push({ id: Number(this.field.get("options")[0].value) });
      } else {
        this.model.set(this.field.get("name"), this.field.get("options")[0].value);
      }
    } else {
      this.field.set("forceFirstSelected", false);
    }

    InputControl.prototype.render.apply(this, arguments);

    /**appChannel.on('destroy:backform', function() {
        that._destroySelectField();
        appChannel.off('destroy:backform');
      }); -> throws error: instance is undefined */

    this._destroySelectField();

    this.select2Options = that.field.get("select2Options") || {};

    this.selectField = this.$el.find("select").select2({
      placeholder: this.field.get("placeholder") ? this.field.get("placeholder") : "",
      maximumSelectionLength:
        typeof this.field.get("hasMultiple") !== "undefined" ? this.field.get("hasMultiple")() : 0,
      // dropdownParent : $('body') // WAS: closest('.inb-form__controllClass')
      ...this.select2Options,
    });

    this.selectField.on("select2:open", function (e: any): void {
      // Attach icon-link to add-entity-form besides input-field

      // Unfortunately, the dd-field is out of that.$el scope
      // -1 for select2... (again)
      const container: JQuery<HTMLElement> = $(".select2-container--open");

      if (
        typeof container !== "undefined" &&
        typeof that.field.get("addEntityForm") !== "undefined" &&
        container.find(".select2-search--dropdown .fi").length === 0
      ) {
        container
          .find(".select2-search__field")
          .after(
            '<i class="fi fi-plus add-entity-icon" title="' +
              that.field.get("addEntityForm").title +
              '" style="position:relative;float:right;color:#e27b4d;margin-top:-35px;margin-right:30px;padding-right:8px;border-right:1px solid gainsboro;cursor:pointer;"></i>'
          );

        container.find(".fi").click(function () {
          that.field.get("addEntityForm").action.call(that);
          that.selectField.select2("close");
          return false;
        });
      }
    });

    if (this.select2Options.rememberSearch) {
      this.selectField
        .on("select2:open", () => {
          let currentQuery = this.selectField.data("current-query");

          setTimeout(() => {
            if (currentQuery && currentQuery.length) {
              $(".select2-search input").val(currentQuery).trigger("input");
            }
          }, 0);
        })
        .on("select2:closing", () => {
          // Why not try to prevent select2 from closing in order
          // to "fake" a multiselect-functionality?
          this.selectField.data("current-query", $(".select2-search input").prop("value"));
        });
    }

    return this;
  },
  formatter: JSONFormatter,
  getValueFromDOM(): any {
    const selectVal: any = this.$el.find("select").val();

    if (typeof this.model.get(this.field.get("name")) == "object") {
      // return object
      return _.map(selectVal, (val: any): { id: any } => {
        return {
          id: val,
        };
      });
    }

    return this.formatter.toRaw(selectVal, this.model);
  },
}));

let SelectControl: any = (Backform.SelectControl = Control.extend({
  defaults: {
    label: "",
    options: [], // List of options as [{label:<label>, value:<value>}, ...]
    extraClasses: [],
    forceSelectedOption: function () {
      return {};
    },
    helpMessage: null,
  },
  template: _.template(
    [
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <select class="<%=Backform.controlClassName%> <%=extraClasses.join(\' \')%>" name="<%=name%>" value="<%-value%>"<%=disabled ? " disabled" : ""%><%=required ? " required" : ""%>>',
      '   <% if (typeof forceSelectedOption().label !== "undefined") { %>',
      '      <option value="<%- forceSelectedOption().value %>" selected="selected"><%- forceSelectedOption().label %></option>',
      "   <% } %>",
      "   <% for (var i=0; i < options.length; i++) { %>",
      "      <% var option = options[i]; %>",
      '      <option value="<%-formatter.fromRaw(option.value)%>" <%= option.value == rawValue ? "selected=\'selected\'" : ""%> <%=option.disabled ? "disabled=\'disabled\'" : ""%>><%-option.label%></option>',
      "    <% } %>",
      "  </select>",
      "</div>",
      "<% if (helpMessage && helpMessage.length) { %>",
      '<span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "<% } %>",
    ].join("\n")
  ),
  events: _.extend({}, Control.prototype.events, {
    "change select": "onChangeSelect", // use this event to allow aborting selection, it will call the original event handler
    "focus select": "clearInvalid",
  }),
  formatter: JSONFormatter,
  getValueFromDOM: function (): any {
    return this.formatter.toRaw(this.$el.find("select").val(), this.model);
  },
}));

let InputControl: any = (Backform.InputControl = Control.extend({
  defaults: {
    type: "text",
    value: "",
    label: "",
    maxlength: 255,
    extraClasses: [],
    extraAttributes: [],
    helpMessage: null,
    groupLabel: null,
    hasLock: false,
    minValue: null,
    hasOpeningTimes: false,
  },

  template: _.template(
    [
      '<% if (typeof groupLabel !== "undefined" && groupLabel != null){ %>',
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="input-group">',
      '  <span class="input-group-label"><%=groupLabel%></span>',
      '  <input type="<%=type%>" class="<%=Backform.controlClassName + extraClasses.join(\' \')%>" autocomplete="off" name="<%=name%>" autocomplete="false" maxlength="<%=maxlength%>" value="<%-value%>" placeholder="<%-placeholder%>" <%=extraAttributes.join(\' \')%><%=disabled ? " disabled" : ""%><%=required ? " required" : ""%><%=pattern ? " pattern=\'"+pattern+"\'" : ""%> />',
      "</div>",
      '<% } else if (typeof groupLabelBackwards !== "undefined" && groupLabelBackwards !== null){ %>',
      '  <label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '  <div class="input-group">',
      '    <input type="<%=type%>" class="<%=Backform.controlClassName + extraClasses.join(\' \')%>"  autocomplete="off"  name="<%=name%>" autocomplete="false" maxlength="<%=maxlength%>" value="<%-value%>" placeholder="<%-placeholder%>" <%=extraAttributes.join(\' \')%><%=disabled ? " disabled" : ""%><%=required ? " required" : ""%><%=pattern ? " pattern=\'"+pattern+"\'" : ""%> />',
      '  <% if (hasLock) { %> <div class="r-contingent-lock"><i class="fi fi-lock"></i></div><% } %>' +
        '    <span class="input-group-label"><%=groupLabelBackwards%></span>',
      "  </div>",
      "  <% if (helpMessage && helpMessage.length) { %>",
      '      <span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "  <% } %>",
      "<% } else { %>",
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <input type="<%=type%>" class="<%=Backform.controlClassName + extraClasses.join(\' \')%>" autocomplete="off" name="<%=name%>" autocomplete="false" maxlength="<%=maxlength%>" value="<%-value%>" placeholder="<%-placeholder%>" <%=extraAttributes.join(\' \')%> <%=disabled ? " disabled" : ""%><%=required ? " required" : ""%><%=pattern ? " pattern=\'"+pattern+"\'" : ""%><%=typeof minValue !== "undefined" && minValue ? " min=\'"+minValue+"\'" : "" %> />',
      "</div>",
      "</div>",
      "<% if (helpMessage && helpMessage.length) { %>",
      '  <span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "<% } %>",
      "<% } %>",
    ].join("\n")
  ),

  events: _.extend({}, Control.prototype.events, {
    "change input": "onChange",
    "focus input": "clearInvalid",
    "focusout input": "_setManualOverride",
    "click .r-contingent-lock": "_unsetManualOverride",
  }),
  onRender(): void {
    if (this.field.get("hasLock")) {
      this.$(".r-contingent-lock").toggleClass("hide", !this.model.get("manual_override"));
    }
  },
  _setManualOverride(ev: any): void {
    if (
      this.field.get("hasLock") &&
      this.$("input").val() != null &&
      this.$("input").val() != "" &&
      this.model.get("tariff_price") != this.$("input").val()
    ) {
      this.model.set("manual_override", true);
    }
  },
  _unsetManualOverride(): void {
    this.model.set("manual_override", false);
  },
  initialize(options: any): void {
    if (typeof options == "object" && options.field && options.field.get("type") == "number") {
      this.formatter = JSONFormatter;
    }
    Control.prototype.initialize.apply(this, arguments);
  },
  getValueFromDOM() {
    return this.formatter.toRaw(this.$el.find("input").val(), this.model);
  },
}));

let PasswordControl: any = (Backform.PasswordControl = InputControl.extend({
  template: _.template(
    [
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <input type="password" class="<%=Backform.controlClassName%> autocomplete="off" <%=extraClasses.join(\' \')%>" <%=extraAttributes.join(\' \')%> name="<%=name%>" autocomplete="false" maxlength="<%=maxlength%>" value="<%-value%>" placeholder="<%-placeholder%>"<%=disabled ? " disabled" : ""%><%=required ? " required" : ""%><%=pattern ? " pattern=\'"+pattern+"\'" : ""%> />',
      "</div>",
      "<% if (helpMessage && helpMessage.length) { %>",
      '  <span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "<% } %>",
    ].join("\n")
  ),
}));

let BooleanControl: any = (Backform.BooleanControl = InputControl.extend({
  defaults: {
    type: "checkbox",
    label: "",
    extraClasses: [],
    helpMessage: null,
    showAsToggle: true,
    forceActive: false,
    prefixLabel: null,
    float: false,
  },

  template: _.template(
    [
      "<% if (showAsToggle && !float) { %>",
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="switch tiny <%=Backform.controlsClassName%>">',
      '  <input class="switch-input <%=extraClasses.join(\' \')%>" id="<%=name%>Switch" type="checkbox" name="<%=name%>" <%=value || forceActive ? "checked=\'checked\'" : ""%> <%=disabled ? "disabled" : ""%> <%=required ? "required" : ""%>>',
      '  <label class="switch-paddle" for="<%=name%>Switch">',
      '    <span class="show-for-sr">Toggle content</span></label>',
      "    <% if (helpMessage && helpMessage.length) { %>",
      '    <br /><span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "    <% } %>",
      "</div>",
      "<% } else if (showAsToggle && float) { %>",
      '<div class="switch switch-float tiny <%=Backform.controlsClassName%>">',
      '  <input class="switch-input <%=extraClasses.join(\' \')%>" id="<%=name%>Switch" type="checkbox" name="<%=name%>" <%=value || forceActive ? "checked=\'checked\'" : ""%> <%=disabled ? "disabled" : ""%> <%=required ? "required" : ""%>>',
      '  <label class="switch-paddle" for="<%=name%>Switch">',
      '    <span class="show-for-sr">Toggle content</span></label>',
      "    <% if (helpMessage && helpMessage.length) { %>",
      '    <br /><span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "    <% } %>",
      "</div>",
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      "<% } else { %>",
      "<% if (prefixLabel) { %>",
      '<label class="<%=Backform.controlLabelClassName%>"><%=prefixLabel%></label>',
      "<% } %>",
      '<div class="<%=Backform.controlsClassName%>">',
      '  <div class="checkbox">',
      "    <label>",
      '      <input type="<%=type%>" class="<%=extraClasses.join(\' \')%>" name="<%=name%>" autocomplete="false" <%=value ? "checked=\'checked\'" : ""%> <%=disabled ? "disabled" : ""%> <%=required ? "required" : ""%> /> <%=label%>',
      "    </label>",
      "    <% if (helpMessage && helpMessage.length) { %>",
      '      <span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "    <% } %>",
      "  </div>",
      "</div>",
      "<% } %>",
    ].join("\n")
  ),

  events: {
    "click input": "setValue",
    "click label": "setValue",
  },
  setValue(): boolean {
    // Required as usual onChange method is not triggered rightfully
    const val: any = this.$el.find("input:checked").val();
    this.model.attributes[this.field.get("name")] = typeof val === "undefined";
    this.model.trigger("change:" + this.field.get("name"));
    return true;
  },
  getValueFromDOM: function () {
    return this.formatter.toRaw(this.$el.find("input").is(":checked"), this.model);
  },
  renderAsteriskIfRequired: function () {
    if (this.showAsterisk) {
      this.$el.find(".checkbox label").append(" *");
    }
  },
}));

let CheckboxControl: any = (Backform.CheckboxControl = BooleanControl);

let RadioControl: any = (Backform.RadioControl = InputControl.extend({
  defaults: {
    type: "radio",
    label: "",
    options: [],
    extraClasses: [],
    helpMessage: null,
  },
  template: _.template(
    [
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="<%=Backform.controlsClassName%>">',
      "  <% for (var i=0; i < options.length; i++) { %>",
      "    <% var option = options[i]; %>",
      '    <label class="<%=Backform.radioLabelClassName%>">',
      '      <input type="<%=type%>" class="<%=extraClasses.join(\' \')%>" name="<%=name%>" value="<%-formatter.fromRaw(option.value)%>" <%=rawValue == option.value ? "checked=\'checked\'" : ""%> <%=disabled ? "disabled" : ""%> <%=required ? "required" : ""%> /> <%-option.label%>',
      "    </label>",
      "  <% } %>",
      "  <% if (helpMessage && helpMessage.length) { %>",
      '    <span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "  <% } %>",
      "</div>",
    ].join("\n")
  ),
  formatter: JSONFormatter,
  getValueFromDOM: function () {
    return this.formatter.toRaw(this.$el.find("input:checked").val(), this.model);
  },
}));
_.extend(Backform, {
  radioControlsClassName: "radio",
  radioLabelClassName: "radio-inline",
});

let AppointmentpickerControl: any = (Backform.AppointmentpickerControl = InputControl.extend({
  defaults: {
    type: "text",
    label: "",
    extraClasses: [],
    maxlength: 255,
    helpMessage: null,
    groupLabel: '<i class="fi fi-calendar"></i>',
    options: {
      //maxDate: null,
      clientId: null,
      contingentId: null,
      start: null,
    },
  },

  events: _.extend({}, Control.prototype.events, {
    /*'blur input': 'onChange',
      'change input': 'onChange',
      'changeDate input': 'onChange',
      'focus input': 'clearInvalid'*/
  }),

  _render: false,

  _destroyDatepicker(): void {
    if (typeof this.appointmentPicker !== "undefined") {
      this.appointmentPicker.destroy();
    }
  },

  initialize(options: any): void {
    InputControl.prototype.initialize.apply(this, arguments);
  },

  render(): any {
    if (this._render) {
      // Avoid auto-render after initial call (we only want to render
      // when the contextual contingent has resourcetype_id === 4)

      InputControl.prototype.render.apply(this, arguments);

      this.field.set(this.field.attributes.options(this.model));

      const that: any = this;

      const destroyBackform: () => void = () => {
        that._destroyDatepicker();
        appChannel.off("destroy:backform", destroyBackform);
      };

      appChannel.on("destroy:backform", destroyBackform);

      this.appointmentPicker = new AppointmentPicker({
        el: this.el,
        options: {
          clientId: this.field.get("client_id"),
          contingent: this.field.get("contingent"),
          triggerHelperCollectionFetch: this.field.get("triggerHelperCollectionFetch"),
        },
        model: this.model,
      });
    }

    return this;
  },
}));

let DatepickerControl: any = (Backform.DatepickerControl = InputControl.extend({
  defaults: {
    type: "text",
    label: "",
    options: {},
    extraClasses: [],
    maxlength: 255,
    helpMessage: null,
    showTimepicker: true,
    groupLabel: '<i class="fi fi-calendar"></i>',
    format: "D. MMMM YYYY",
    minDate: null,
    maxDate: null,
    onSelectCallback: null,
  },

  events: {
    // :not([type="time"])
    "focusout input": "_onChange",
    "focusin input": "_onClick",
  },

  _onClick(ev: any): void {
    //     console.log("maxLength " + this.defaults.maxlength);
    //    console.log("something: " + this.maxlength);
  },

  _onChange(ev: any): void {
    ev.preventDefault();
    const settings: {
      API_HOST: string;
      INVOICE_API_HOST: string;
      API_HOST_PUBLIC: string;
      API_HOST_STAGING: string;
      INVOICE_API_HOST_STAGING: string;
      API_HOST_STAGING_PUBLIC: string;
      API_HOST_TEST: string;
      INVOICE_API_HOST_TEST: string;
      API_HOST_TEST_PUBLIC: string;
      GOOGLE_MAPS_API_KEY: string;
      language: string;
    } = Sync.Settings;
    const field: any = this.field.get("name"),
      form: any = this.$el, //$(ev.currentTarget).closest('.inb-form-group'),
      date: any = Inboomai18n._changeLocaleOfMonth(
        settings.language,
        form.find('input[name="' + field + '"]').val()
      ),
      time: any =
        form.find(".datepicker-time").length >= 1 ? form.find(".datepicker-time").val() : "",
      onlyTimeChanged: boolean = time !== moment(this.model.get(field)).format("HH:mm");
    $('input[name="' + field + '"]').val(date);
    if (!_.isNull(date) && !_.isEmpty(date)) {
      if (!$(ev.currentTarget).hasClass("datepicker-date")) {
        // Do not trigger update when it's a time field
        this.model._modelUpdatedByUser = true;
      }

      let dateMoment: any;

      if (this.field.get("format") === "MMMM YYYY") {
        // is month picker
        dateMoment = moment(date, "MMMM YYYY");
      } else {
        if (time !== "") {
          dateMoment = moment(date + " " + time, "D. MMMM YYYY HH:mm");
        } else {
          dateMoment = moment(date, "D. MMMM YYYY");
        }
      }
      const apiFormattedDate: string = dateMoment.clone().format("YYYY-MM-DD HH:mm") + ":00";

      this.model.set(field, apiFormattedDate, { silent: true });

      this.model.trigger("backform:change:date", field, onlyTimeChanged);

      // Re-render this field as any invalid input-characters have been sorted out
      // in apiFormattedDate and need therefore to be attached ASAP
      this.render();

      if (this.model.get("id") == "" && this.field.get("triggerPicker") != "undefined") {
        this.model.set(
          this.field.get("triggerPicker"),
          dateMoment.clone().format("YYYY-MM-DD") +
            " " +
            moment(this.model.get(this.field.get("triggerPicker")), "YYYY-MM-DD HH:mm").format(
              "HH:mm"
            )
        );
        // @todo At least for appointmentsForm, this should become
        // the min-date value for the sibling field
      }

      if (
        !_.isUndefined(this.field.get("onSelectCallback")) &&
        _.isFunction(this.field.get("onSelectCallback"))
      ) {
        this.field.get("onSelectCallback")(dateMoment, apiFormattedDate);
      }
    }
  },

  _destroyDatepicker() {
    if (typeof this.datepicker !== "undefined") {
      this.datepicker.destroy();
    }
  },

  render(): any {
    const that: any = this,
      val: any = this.model.get(this.field.get("name"));
    const settings: {
      API_HOST: string;
      INVOICE_API_HOST: string;
      API_HOST_PUBLIC: string;
      API_HOST_STAGING: string;
      INVOICE_API_HOST_STAGING: string;
      API_HOST_STAGING_PUBLIC: string;
      API_HOST_TEST: string;
      INVOICE_API_HOST_TEST: string;
      API_HOST_TEST_PUBLIC: string;
      GOOGLE_MAPS_API_KEY: string;
      language: string;
    } = Sync.Settings;
    if (typeof this._latestVal !== "undefined" && this._latestVal == val) {
      // Stop from unnecessarily re-rendering
      return this;
    }

    if (typeof this.field.get("format") === "undefined") {
      this.field.set("format", "D. MMMM YYYY");
    }

    this._latestVal = val;

    InputControl.prototype.render.apply(this, arguments);

    this._destroyDatepicker();

    this.datepicker = new Datepicker({
      field: this.$el.find("input")[0],
      trigger: this.$el.find("input")[0],
      format: this.field.get("format"),
      i18n: Inboomai18n.getPikadayI18n(),
      firstDay: 1, // 0 = sunday, 1 = monday, ...
      minDate: this.field.get("minDate"),
      maxDate: this.field.get("maxDate"),
      toString: function (date) {
        let parts: (string | any | number)[] = [
          date.getDate() + ".",
          Inboomai18n._changeLocaleOfMonth(settings.language, date.getMonth()),
          date.getFullYear(),
        ];
        return parts.join(" ");
      },
      /* onDraw : (self) => {

            if (that.field.get('format') !== 'MMMM YYYY') return;

            // Display datepicker as plain month picker
            self.el.querySelector('.pika-table').classList.add('hide');

            const monthSelect = self.el.querySelector('.pika-select-month'),
                  yearSelect  = self.el.querySelector('.pika-select-year');

            monthSelect.addEventListener('change', (a,b) => {

             //that.model.set(that.field.get('name'), '01-' + (a.target.value) + 1 + '2019');
            
            //self.el.querySelector('[data-day="1"]').click();
            });

            yearSelect.addEventListener('change', () => {
                self.el.querySelector('[data-day="1"]').click();
            });

        }*/
    });

    this.datepicker.el.classList.add("inbooma-month-picker");

    const destroyBackform: () => void = function (): void {
      that._destroyDatepicker();
      appChannel.off("destroy:backform", destroyBackform);
    };

    appChannel.on("destroy:backform", destroyBackform);
    const inputValue: any = this.model.get(this.field.get("name")),
      isEmptyValueCase: any = !inputValue && this.field.get("allowEmpty"),
      d: any = moment(inputValue);
    if (d.isValid()) {
      // Only change field when model is unsaved
      this.datepicker.setDate(d.format("Y-MM-DD"));
    } else if (!isEmptyValueCase) {
      // Auto-set to today
      this.model.set(this.field.get("name"), moment().format("YYYY-MM-DD HH:mm"));
    }

    return this;
  },
}));

let DatetimepickerControl: any = (Backform.DatetimepickerControl = DatepickerControl.extend({
  // @todo if you read this in 2020+ : add step="5" attribute to input type="time"
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time
  template: _.template(
    [
      '<% let _time = (moment(value).isValid() ? moment(value).format("HH:mm") : "") %>',
      '<label class="<%=Backform.controlLabelClassName%>"><%=label%></label>',
      '<div class="input-group">',
      '  <span class="input-group-label"><%=groupLabel%></span>',
      '  <input type="<%=type%>" class="datepicker-date <%=Backform.controlClassName%> <%=extraClasses.join(\' \')%>" autocomplete="off" name="<%=name%>" maxlength="<%=maxlength%>" value="<%-value%>" placeholder="<%-placeholder%>"<%=disabled ? " disabled" : ""%><%=required ? " required" : ""%><%=pattern ? " pattern=\'"+pattern+"\'" : ""%> />',
      '  <input type="time" data-abide-ignore class="datepicker-time" autocomplete="off" name="datepicker-time" maxlength="<%=maxlength%>" value="<%-_time%>" autocomplete="false" placeholder="<%-placeholder%>"<%=disabled ? " disabled" : ""%> />',
      '  <% if (typeof openingTimesModel !== "undefined") { %>' +
        '  <i class="open-datepicker fi fi-info"></i>' +
        "  <% } %>" +
        "</div>",
    ].join("\n")
  ),

  // Removed type="time" from .datepicker-time as this was triggering a
  // disturbing time-selector in Edge browser

  initialize(): void {
    this.events["focusin input.datepicker-time"] = "_onFocusInTime";
    this.events["focusout input.datepicker-time"] = "_onFocusOutTime";
    this.events["click .open-datepicker"] = "_toggleOpeningTimesTable";

    DatepickerControl.prototype.initialize.apply(this, arguments);
  },

  _openingTimesTableOpen: false,

  _toggleOpeningTimesTable(ev: any): void {
    if (this._openingTimesTableOpen) {
      this._onFocusOutTime(ev);
    } else {
      this._onFocusInTime(ev);
    }
  },

  _onFocusInTime(ev: any): void {
    ev.preventDefault();

    const that: any = this;
    const t: any = this.model._t;

    if (
      typeof this.field.get("openingTimesModel") !== "undefined" &&
      !_.isEmpty(this.field.get("openingTimesModel"))
    ) {
      this._openingTimesTableOpen = true;

      let timeInput: any = ev.target;
      const openLocation = this.field.get("openingTimesModel");

      if (typeof timeInput !== "undefined") {
        const i18n_weekdays: {
          [key: string]: string;
        } = {
          // external key: local i18n library key
          // Also required for sorted output (week starts with monday)
          // Of course we could use moment().format('dd') for that but
          // let's use our very own i18n... :)
          mon: "monday",
          tue: "tuesday",
          wed: "wednesday",
          thu: "thursday",
          fri: "friday",
          sat: "saturday",
          sun: "sunday",
        };

        let openingTimesTable = `
            <div class="openingtimes-popup pika-single is-bound">
                <div class="pika-title">
                  <div class="pika-label">
                    ${t("opening_times_for")} ${openLocation.get("name")}
                  </div>
                </div>
          `;

        // Get active season, then render table-popup
        const today: any = moment(this.model.get(this.field.get("name"))),
          openingTimes = OpeningTimes.getOpeningTimes(openLocation.get("check_in_periods"), today);

        if (
          typeof openingTimes.activeSeason === "undefined" ||
          typeof openingTimes.activeSeason === "undefined" ||
          openingTimes.activeSeason.length === 0
        ) {
          openingTimesTable += '<p style="text-align:center;">';

          if (typeof openingTimes.nextAvailableSeason !== "undefined") {
            openingTimesTable += `${t(
              "next_available_opening_time"
            )} ${openingTimes.nextAvailableSeasonPeriod.format("dd, D.MM.YY")}`;
          } else {
            if (typeof openingTimes.activeSeasonPeriod !== "undefined") {
              openingTimesTable += "</p>";
              openingTimesTable += `<p style="text-align:center;">${t(
                "closed_from_date",
                null,
                "capital"
              )} ${openingTimes.activeSeasonPeriod.format("Do MMMM YYYY")}</p>`;
              openingTimesTable += '<p style="text-align:center;">';
            }
            openingTimesTable += t("no_opening_times");
          }

          openingTimesTable += "</p>";
        } else {
          openingTimesTable += `<p style="text-align:center;">${t(
            "valid_from_date",
            null,
            "capital"
          )} ${openingTimes.activeSeasonPeriod.format("Do MMMM YYYY")}</p>`;
          openingTimesTable += "<table>";

          let c: number = 0; // To highlight given day

          _.each(i18n_weekdays, (fullDayName: string, dayName: string): void => {
            if (typeof openingTimes.activeSeason[dayName] !== "undefined") {
              openingTimesTable +=
                `
                <tr class="day-table-row" style="` +
                (c++ == Number(today.format("e")) ? "font-weight:bold;" : "") +
                `">
                    <td class="day-column">
                      ${t(i18n_weekdays[dayName], "capital").substring(0, 2)}.
                    </td>
                    <td class="day-time-column">`;

              const sortedTimes = _.sortBy(openingTimes.activeSeason[dayName], (openingTime) => {
                return Number(openingTime["begin"].replace(":", ""));
              });

              _.each(sortedTimes, function (time, key) {
                openingTimesTable +=
                  (key >= 1 ? "<br />" : "") +
                  `
                      <span
                       title="${t("click_to_select_time")}"
                       data-begin="${time.begin}"
                       data-end="${time.end}">
                          ${time.begin} - ${time.end}
                      </span>`;
              });

              openingTimesTable += `</td></tr>`;
            }
          });
        }

        openingTimesTable += `</table></div>`;

        timeInput = $(timeInput).parent();

        timeInput.append(openingTimesTable);

        timeInput.find(".day-time-column span").on("mousedown", function (_ev: any): void {
          // Sorry for this non-non-incremental way of attaching events... :(
          that.$el
            .find(".datepicker-time")
            .val(_ev.currentTarget.getAttribute("data-" + that.field.get("name")));

          that._onFocusOutTime();
          that._onChange(_ev); // Setting the new time value to model will be handled in DatepickerControl._onChange
        });
      }
    }
  },

  _onFocusOutTime(): void {
    this._openingTimesTableOpen = false;
    $(".openingtimes-popup").remove();
  },

  render(): any {
    DatepickerControl.prototype.render.apply(this, arguments);

    if (typeof this.field.get("allowOnlyTime") !== "undefined") {
      this.$el.find(".datepicker-date").attr("disabled", "disabled");
    }

    return this;
  },
}));

let ButtonControl: any = (Backform.ButtonControl = Control.extend({
  defaults: {
    type: "submit",
    label: "Submit",
    status: undefined, // error or success
    message: undefined,
    helpMessage: undefined,
    extraClasses: [],
  },
  template: _.template(
    [
      '<label class="hide <%=Backform.controlLabelClassName%>">&nbsp;</label>',
      '<div class="<%=Backform.controlsClassName%>">',
      '  <button type="<%=type%>" name="<%=name%>" class="button <%=extraClasses.join(\' \')%>" <%=disabled ? "disabled" : ""%> ><%=label%></button>',
      '  <% var cls = ""; if (status == "error") cls = Backform.buttonStatusErrorClassName; if (status == "success") cls = Backform.buttonStatusSuccessClassname; %>',
      '  <span class="status <%=cls%>"><%=message%></span>',
      "  <% if (helpMessage && helpMessage.length) { %>",
      '    <span class="<%=Backform.helpMessageClassName%>"><%=helpMessage%></span>',
      "  <% } %>",
      "</div>",
    ].join("\n")
  ),
  initialize() {
    Control.prototype.initialize.apply(this, arguments);
    this.listenTo(this.field, "change:status", this.render);
    this.listenTo(this.field, "change:message", this.render);
  },
}));

_.extend(Backform, {
  buttonStatusErrorClassName: "text-danger",
  buttonStatusSuccessClassname: "text-success",
});

export default Backform;
